import { db } from '../../firebase';
import { addDoc, collection, serverTimestamp, query, where, getDocs, limit } from 'firebase/firestore';

/**
 * Registra un acceso (inicio de sesión exitoso) en el historial del servicio.
 *
 * Escribe un documento inmutable en `servicios/{servicioId}/accesos`. Es
 * best-effort: cualquier error se traga en silencio para NO romper ni bloquear
 * el flujo de login.
 *
 * Para los accesos al DASHBOARD el usuario es un admin/inspector/administrativo;
 * se enriquece `nombre`/`apellidos` leyendo su doc de `admins` (por `correo`).
 * Los admins no pertenecen a una empresa/delegación única, así que esos campos
 * quedan vacíos (esos accesos solo los ve un administrador — ver `accesoVisible`).
 *
 * @param {Object} params
 * @param {string} params.servicioId - Servicio donde se guarda el acceso.
 * @param {Object} params.user - Usuario de Firebase Auth (email, uid).
 * @param {string} [params.origen='dashboard'] - 'dashboard' | 'web' | 'movil'.
 * @param {Object} [params.extra] - Campos ya resueltos (nombre, apellidos, codEmpresa, empresaNombre, delegacion).
 */
export const registrarAcceso = async ({ servicioId, user, origen = 'dashboard', extra = {} }) => {
  try {
    if (!servicioId || !user) return;
    const email = (user.email || '').toLowerCase().trim();

    const datos = {
      nombre: extra.nombre || '',
      apellidos: extra.apellidos || '',
      codEmpresa: extra.codEmpresa || '',
      empresaNombre: extra.empresaNombre || '',
      delegacion: extra.delegacion || '',
    };

    // Enriquecimiento best-effort desde la ficha de admin: nombre y, para que
    // el acceso sea visible dentro del scope de administrativos/inspectores
    // (ver accesoVisible), su empresa y delegación asignadas (la primera de
    // cada lista). Sin empresas/delegaciones asignadas quedan vacíos y el
    // acceso solo lo ve un administrador, como hasta ahora.
    if (email && (!datos.nombre || !datos.codEmpresa || !datos.delegacion)) {
      try {
        const adminSnap = await getDocs(query(
          collection(db, 'servicios', servicioId, 'admins'),
          where('correo', '==', email),
          limit(1)
        ));
        if (!adminSnap.empty) {
          const a = adminSnap.docs[0].data() || {};
          if (!datos.nombre) {
            datos.nombre = a.nombre || '';
            datos.apellidos = a.apellidos || '';
          }
          if (!datos.codEmpresa && Array.isArray(a.empresasAsignadas) && a.empresasAsignadas.length > 0) {
            datos.codEmpresa = String(a.empresasAsignadas[0]);
          }
          if (!datos.delegacion && Array.isArray(a.delegacionesAsignadas) && a.delegacionesAsignadas.length > 0) {
            datos.delegacion = String(a.delegacionesAsignadas[0]);
          }
          if (!datos.empresaNombre && datos.codEmpresa) {
            try {
              const empSnap = await getDocs(query(
                collection(db, 'servicios', servicioId, 'empresas'),
                where('codEmpresa', '==', datos.codEmpresa),
                limit(1)
              ));
              if (!empSnap.empty) datos.empresaNombre = empSnap.docs[0].data().nombre || '';
            } catch (_e) { /* opcional */ }
          }
        }
      } catch (_e) { /* enriquecimiento opcional */ }
    }

    await addDoc(collection(db, 'servicios', servicioId, 'accesos'), {
      email,
      uid: user.uid || '',
      origen,
      timestamp: serverTimestamp(),
      ...datos,
    });
  } catch (_e) {
    // best-effort: nunca romper el login.
  }
};
